import { useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowUp } from "lucide-react";

import { Button } from "@dashai/components/ui/button";
import { Input } from "@dashai/components/ui/input";

interface PanelComposerProps {
  dashboardName: string;
  disabled: boolean;
  /** A question that failed, put back in the box so it can be sent again. */
  restore: string | null;
  /** Replaces the default prompt, such as while the panel checks the view. */
  placeholder?: string;
  onSend: (question: string) => void;
}

export function PanelComposer({
  dashboardName,
  disabled,
  restore,
  placeholder,
  onSend,
}: PanelComposerProps) {
  const [text, setText] = useState("");
  const restored = useRef<string | null>(null);

  // Put a failed question back in the box, and take it out again once it is
  // sent, unless the user has typed something else since.
  useEffect(() => {
    if (restore) {
      setText(restore);
      restored.current = restore;
    } else if (restored.current !== null) {
      const previous = restored.current;
      setText((current) => (current === previous ? "" : current));
      restored.current = null;
    }
  }, [restore]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const question = text.trim();
    if (!question || disabled) return;
    onSend(question);
    setText("");
  };

  return (
    <form onSubmit={submit} className="flex items-center gap-2 border-t border-border p-3">
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder ?? `Ask about ${dashboardName}…`}
        aria-label={`Ask about ${dashboardName}`}
        disabled={disabled}
        className="h-10 rounded-xl"
      />
      <Button
        type="submit"
        size="icon"
        className="h-10 w-10 shrink-0 rounded-xl"
        disabled={disabled || !text.trim()}
        aria-label="Send"
      >
        <ArrowUp className="h-4 w-4" />
      </Button>
    </form>
  );
}
